import type { StudioSystemType } from 'types/studio/items'
import { getRoofName } from '../nativeMounting/utils'
import { getVanDerValkPitchedCladdingOptionsFromSystem } from './applyVanDerValkPitchedCladdingOptions'
import { getVanDerValkPitchedCladdingForRoofType } from './pitchedCladding'
import {
  buildPitchedCladdingOptionsCacheKey,
  getStandingSeamTypeFromPitchedCladdingOptions,
  isVanDerValkPitchedCladdingType,
} from './pitchedCladdingFieldConfig'
import { VANDERVALK_PITCHED_SUITABILITY_GROUPING_VERSION } from './suitabilityOptions'
import type { VanDerValkSuitabilityModuleSpecs } from './types'
import { sortVanDerValkModuleGrids } from './vanDerValkModuleGrids'

/**
 * Van der Valk flat-roof suitability API expects width, length, thickness in mm.
 * Studio `ModuleType` uses width/height in metres and thickness in mm (catalog).
 * API "length" maps to the module's height edge (second in-plane dimension).
 */
export type VanDerValkSuitabilityErrorReason =
  | 'no_module_grids'
  | 'no_module_type'
  | 'missing_width'
  | 'missing_length'
  | 'missing_thickness'
  | 'missing_weight'
  | 'invalid_dimensions'

function isValidModuleDimension(value: unknown): value is number {
  return typeof value === 'number' && !Number.isNaN(value)
}
const VDV_MODULE_WEIGHT_MIN_KG = 1
const VDV_MODULE_WEIGHT_MAX_KG = 500

/** Valid module mass (kg) for vendor BOM, or null when catalog weight is missing/invalid. */
function parseModuleWeightKgFromCatalog(mod: { weight?: number }): number | null {
  const raw = mod.weight
  if (raw === undefined || raw === null || String(raw).trim() === '') {
    return null
  }
  const n = typeof raw === 'number' ? raw : Number(String(raw).trim())
  if (!Number.isFinite(n) || Number.isNaN(n) || n <= 0) {
    return null
  }
  const rounded = Math.round(n)
  if (rounded < VDV_MODULE_WEIGHT_MIN_KG) {
    return null
  }
  return Math.min(VDV_MODULE_WEIGHT_MAX_KG, rounded)
}

export type GetVanDerValkSuitabilityPayloadResult =
  | {
      ok: true
      payload: { width: number; length: number; thickness: number; moduleWeightKg: number }
      moduleSpecs: VanDerValkSuitabilityModuleSpecs
    }
  | { ok: false; reason: VanDerValkSuitabilityErrorReason }

type FirstValidResult =
  | { ok: false; reason: VanDerValkSuitabilityErrorReason }
  | {
      ok: true
      payload: { width: number; length: number; thickness: number; moduleWeightKg: number }
      moduleSpecs: VanDerValkSuitabilityModuleSpecs
    }

function findFirstValidVanDerValkSuitabilityModule(system: StudioSystemType | null | undefined): FirstValidResult {
  if (!system) {
    return { ok: false, reason: 'no_module_grids' }
  }

  const grids = system.moduleGrids()
  if (!grids?.length) {
    return { ok: false, reason: 'no_module_grids' }
  }

  const sortedGrids = sortVanDerValkModuleGrids(grids)

  let sawNoModule = false
  let sawMissingWidth = false
  let sawMissingLength = false
  let sawModuleWithoutThickness = false
  let sawModuleWithoutWeight = false

  for (const grid of sortedGrids) {
    const mod = grid.moduleType()
    if (mod == null) {
      sawNoModule = true
      continue
    }
    if (!isValidModuleDimension(mod.width)) {
      sawMissingWidth = true
      continue
    }
    /** Studio `height` (m) is sent to the API as `length` (mm). */
    if (!isValidModuleDimension(mod.height)) {
      sawMissingLength = true
      continue
    }

    const widthMm = Math.round(mod.width * 1000)
    const lengthMm = Math.round(mod.height * 1000)
    if (widthMm < 1 || lengthMm < 1) {
      return { ok: false, reason: 'invalid_dimensions' }
    }

    const t = mod.thickness
    if (t === undefined || t === null || Number.isNaN(Number(t)) || Number(t) < 1) {
      sawModuleWithoutThickness = true
      continue
    }

    const thickness = Math.round(Number(t))
    if (thickness < 1) {
      sawModuleWithoutThickness = true
      continue
    }

    const moduleWeightKg = parseModuleWeightKgFromCatalog(mod)
    if (moduleWeightKg == null) {
      sawModuleWithoutWeight = true
      continue
    }

    const moduleSpecs: VanDerValkSuitabilityModuleSpecs = {
      id: String(mod.id ?? ''),
      module_id: mod.module_id ?? null,
      code: String(mod.code ?? ''),
      width: widthMm,
      length: lengthMm,
      thickness,
      weightKg: moduleWeightKg,
    }

    return {
      ok: true,
      payload: { width: widthMm, length: lengthMm, thickness, moduleWeightKg },
      moduleSpecs,
    }
  }

  if (sawModuleWithoutThickness) {
    return { ok: false, reason: 'missing_thickness' }
  }
  if (sawMissingWidth) {
    return { ok: false, reason: 'missing_width' }
  }
  if (sawMissingLength) {
    return { ok: false, reason: 'missing_length' }
  }
  if (sawNoModule) {
    return { ok: false, reason: 'no_module_type' }
  }
  if (sawModuleWithoutWeight) {
    return { ok: false, reason: 'missing_weight' }
  }
  return { ok: false, reason: 'no_module_type' }
}

export function getVanDerValkSuitabilityPayload(
  system: StudioSystemType | null | undefined
): GetVanDerValkSuitabilityPayloadResult {
  const r = findFirstValidVanDerValkSuitabilityModule(system)
  if (!r.ok) {
    return r
  }
  return { ok: true, payload: r.payload, moduleSpecs: r.moduleSpecs }
}

export function getVanDerValkSuitabilityModuleSpecs(
  system: StudioSystemType | null | undefined
): VanDerValkSuitabilityModuleSpecs | null {
  const r = findFirstValidVanDerValkSuitabilityModule(system)
  if (!r.ok) {
    return null
  }
  return r.moduleSpecs
}

export function areVanDerValkSuitabilityModuleSpecsEqual(
  a: VanDerValkSuitabilityModuleSpecs | null | undefined,
  b: VanDerValkSuitabilityModuleSpecs | null | undefined
): boolean {
  if (a == null || b == null) {
    return false
  }
  return (
    a.id === b.id &&
    a.module_id === b.module_id &&
    a.code === b.code &&
    a.width === b.width &&
    a.length === b.length &&
    a.thickness === b.thickness &&
    a.weightKg === b.weightKg
  )
}

export type VanDerValkCachedSuitabilityContext = {
  suitabilityCladdingType?: string | null
  suitabilitySeamType?: string | null
  suitabilityCladdingOptionsKey?: string | null
  suitabilityGroupingVersion?: number | null
}

/**
 * True when cached suitability metadata matches the current roof/cladding context.
 * Pitched-roof suitability is keyed by cladding type, standing-seam type, and cladding options.
 */
export function isVanDerValkCachedSuitabilityContextCurrent(
  system: StudioSystemType | null | undefined,
  cached: VanDerValkCachedSuitabilityContext | null | undefined
): boolean {
  if (!system || system.mounting_type !== 'onRoof') {
    return true
  }

  const roofTypeUrl = window.projectForm?.getState?.()?.values?.roof_type
  const roofTypeName = getRoofName(roofTypeUrl)
  const claddingType = getVanDerValkPitchedCladdingForRoofType(roofTypeName)
  if (!claddingType || !isVanDerValkPitchedCladdingType(claddingType)) {
    return false
  }

  if (cached?.suitabilityCladdingType !== claddingType) {
    return false
  }

  if (cached?.suitabilityGroupingVersion !== VANDERVALK_PITCHED_SUITABILITY_GROUPING_VERSION) {
    return false
  }

  const pitchedCladdingOptions = getVanDerValkPitchedCladdingOptionsFromSystem(system)

  if (claddingType === 'STANDING_SEAM') {
    const seamType = getStandingSeamTypeFromPitchedCladdingOptions(pitchedCladdingOptions)
    if (!seamType || cached?.suitabilitySeamType !== seamType) {
      return false
    }
  }

  const optionsKey = buildPitchedCladdingOptionsCacheKey(claddingType, pitchedCladdingOptions)
  if (cached?.suitabilityCladdingOptionsKey !== optionsKey) {
    return false
  }

  return true
}

/**
 * True when cached suitability (selection + module snapshot) matches the active module grids
 * so vendor BOM may run. When panel specs or roof/cladding context change, this is false until
 * suitability POST completes.
 */
export function isVanDerValkSuitabilityReadyForBom(system: StudioSystemType | null | undefined): boolean {
  if (!system || system.mounting !== 'vanDerValk') {
    return true 
  }

  const pre = getVanDerValkSuitabilityPayload(system)
  if (!pre.ok) {
    return false
  }

  const vd = (
    system.integration_json as {
      vanDerValk?: VanDerValkCachedSuitabilityContext & {
        suitabilityModuleSpecs?: VanDerValkSuitabilityModuleSpecs | null
        selectedSuitability?: unknown
        suitabilityResult?: unknown
      }
    } | null
  )?.vanDerValk

  if (!vd?.suitabilityModuleSpecs || vd.selectedSuitability == null) {
    return false
  }

  if (!areVanDerValkSuitabilityModuleSpecsEqual(vd.suitabilityModuleSpecs, pre.moduleSpecs)) {
    return false
  }

  if (!isVanDerValkCachedSuitabilityContextCurrent(system, vd)) {
    return false
  }

  const results = vd.suitabilityResult
  if (results == null) {
    return false
  }
  if (Array.isArray(results) && results.length === 0) {
    return false
  }

  return true
}
